import i18n from '@app/localization/i18n';
import slugify from '@utils/url/slugify';
import { capitalize } from '@shared/filters';
import { liveTimeFilterSlug } from '@store/modules/ui/shared/timeFilter/utils';

export const OFFER_PREFIX = `/${slugify(i18n.t('sport bets').toString())}`;
export const LOTTERY_PREFIX = `/${slugify(i18n.t('lotto bets').toString())}`;
export const LOTTERY_GAME_PREFIX = `${slugify(i18n.t('game').toString())}`;
export const LOTTERY_SYSTEM_PREFIX = `${slugify(i18n.t('systems').toString())}`;
export const SPECIAL_PREFIX = `/${slugify(i18n.t('special bets').toString())}`;

export const VUE_ROUTER_CONTROLLED = 'data-vue-router-controlled';

const accountRouteNames = {
    deposit: 'Deposit',
    history: 'History',
    passwordUpdate: 'Password update',
    profile: 'Profile',
    settings: 'Settings',
    tickets: 'Tickets',
    withdrawal: 'Withdrawal',
    documentUpload: 'Document upload',
};

const accountTitles = {
    deposit: capitalize(i18n.t('deposit').toString()),
    history: capitalize(i18n.t('history').toString()),
    profile: capitalize(i18n.t('profile page').toString()),
    settings: capitalize(i18n.t('settings').toString()),
    tickets: capitalize(i18n.t('tickets').toString()),
    withdrawal: capitalize(i18n.t('withdrawal').toString()),
    passwordUpdate: capitalize(i18n.t('password update').toString()),
    documentUpload: capitalize(i18n.t('document upload').toString()),
};

const accountLinks = {
    deposit: `${slugify(i18n.t('deposit').toString())}`,
    history: `${slugify(i18n.t('history').toString())}/:type?`,
    passwordUpdate: `${slugify(i18n.t('password update').toString())}`,
    settings: `${slugify(i18n.t('settings').toString())}`,
    tickets: `${slugify(i18n.t('tickets').toString())}`,
    withdrawal: `${slugify(i18n.t('withdrawal').toString())}`,
    documentUpload: `${slugify(i18n.t('document upload').toString())}`,
};

export const resultsRouteNames = {
    ticketReprint: 'ticketReprint',
    checkTicket: '/checkTicket',
    sportResults: 'sportResults',
    liveResults: 'liveResults',
    lottoResults: 'lottoResults',
    lottoSystemResults: 'lottoSystemResults',
    lottoGameResults: 'lottoGameResults',
    specialsResults: 'specialsResults',
    agenciesResults: 'agenciesResults',
    virtualResults: 'virtualResults',
    virtualShopsResults: 'virtualShopsResults',
};

export const resultsLinks = {
    ticketReprint: '/ticketReprint',
    checkTicket: '/check-ticket',
    sportResults: `${slugify(i18n.t('sport').toString())}`,
    liveResults: `${slugify(i18n.t('live').toString())}`,
    lottoResults: `${slugify(i18n.t('lottery').toString())}`,
    lottoGameResults: `${slugify(i18n.t('lottery').toString())}/${LOTTERY_GAME_PREFIX}`,
    lottoSystemResults: `${slugify(i18n.t('lottery').toString())}/${LOTTERY_SYSTEM_PREFIX}`,
    specialResults: `${slugify(i18n.t('specials').toString())}`,
    virtualResults: `${slugify(i18n.t('virtual').toString())}`,
    virtualShopsResults: `${slugify(i18n.t('virtual shops').toString())}`,
};

const resultsTitles = {
    sportResults: `${i18n.t('sport results').toString()}`,
    liveResults: `${i18n.t('live results').toString()}`,
    lottoResults: `${i18n.t('lottery results').toString()}`,
    specialResults: `${i18n.t('specials results').toString()}`,
    virtualResults: `${i18n.t('virtual results').toString()}`,
    virtualShopsResults: `${i18n.t('virtual shops results').toString()}`
};

export const links = {
    sportBets: `/${slugify(i18n.t('sport bets').toString())}`,
    landing: '/',
    live: `${OFFER_PREFIX}/${liveTimeFilterSlug}`,
    filter: `/${slugify(i18n.t('filter').toString())}`,
    games: `/${slugify(i18n.t('games').toString())}`,
    casino: `/${slugify(i18n.t('casino live').toString())}`,
    virtual: `/${slugify(i18n.t('virtual bets').toString())}`,
    lottery: `${LOTTERY_PREFIX}`,
    lottoCheckTicket: `${LOTTERY_PREFIX}/check-ticket`,
    special: `${SPECIAL_PREFIX}`,
    search: `/${slugify(i18n.t('search').toString())}`,
    sportFavourites: `/${slugify(i18n.t('favourites').toString())}`,
    teams: `/${slugify(i18n.t('teams').toString())}`,
    results: `/${slugify(i18n.t('results').toString())}`,
    articles: `/${slugify(i18n.t('articles').toString())}`,
    activateAccount: `/${slugify(i18n.t('activate account').toString())}`,
    profile: `/${slugify(i18n.t('profile').toString())}`,
    passwordRecovery: `/${slugify(i18n.t('password recovery', 'ro-RO').toString())}`,
    paymentGatewayStatus: '/dwstatus',
    info: `/${slugify(i18n.t('info').toString())}`,
    shops: `/${slugify(i18n.t('shops').toString())}`,
    matchesLanding: `/${slugify(i18n.t('matches').toString())}`,
    wiki: '/wiki',
    help: `/wiki/${i18n.t('help-url').toString()}`,
    news: `/${slugify(i18n.t('news').toString())}`,
    verifyTicket: `/${slugify(i18n.t('verify').toString())}`,
    page_404: '/404',
    page_401: '/401',
    promotionsInfo: `/${i18n.t('bonuses-promotions').toString()}`,
    register: `/${slugify(i18n.t('register').toString())}`,
    ...accountLinks, // should not be exported. They're missing the router prefix
    ...resultsLinks,
};

export const titles = {
    landing: i18n.t('Sports and live betting'),
    games: i18n.t('Games - slot machines, poker, roulette'),
    casino: i18n.t('Casino Live - Blackjack, Roulette'),
    lottery: i18n.t('Lottery betting - Greece Kino, Win for Life, Numerone'),
    virtual: i18n.t('Virtual betting - Lucky six, Dog racing, Virtual sports'),
    special: i18n.t('Special bets only at Superbet'),
    results: i18n.t('Results of sport events, lotto events'),
    articles: i18n.t('Latest news in world of sport'),
    register: capitalize(i18n.t('register').toString()),
    passwordRecovery: capitalize(i18n.t('forgot password').toString()),
    info: capitalize(i18n.t('info').toString()),
    shops: i18n.t('shops'),
    wiki: 'Wiki',
    news: capitalize(i18n.t('news').toString()),
    verifyTicket: i18n.t('Verify Ticket'),
    notFound: capitalize(i18n.t('page not found').toString()),
    notAuthorized: capitalize(i18n.t('page not authorized').toString()),
    paymentGatewayStatus: capitalize(i18n.t('transaction status').toString()),
    activateAccount: capitalize(i18n.t('activate account').toString()),
    promotionsInfo: capitalize(i18n.t('bonuses promotions').toString()),
    ...accountTitles,
    ...resultsTitles,
};

export const routeNames = {
    landing: 'Landing',
    sports: 'Sports',
    sportOffer: 'Sports/sportOffer',
    filter: 'Filter',
    search: 'Search',
    sportFavourites: 'Sport Favourites',
    casinoLive: 'CasinoLive',
    games: 'Games',
    lotteryGame: 'Lottery Game',
    lotteryLanding: 'Lottery landing',
    lotterySystem: 'Lottery system',
    special: 'Special',
    virtual: 'Virtual',
    virtualGame: 'Virtual Game',
    results: 'Results',
    articles: 'Articles',
    register: 'Register',
    passwordRecovery: 'Password Recovery',
    info: 'Info',
    shops: 'Shops',
    wiki: 'Wiki',
    paymentGatewayStatus: 'Payment gateway',
    activateAccount: 'ActivateAccount',
    matchesLanding: 'LinkLandingPage',
    news: 'NewsPage',
    article: 'ArticlePage',
    verifyTicket: 'Verify Ticket',
    notAuthorized: 'Not Authorized',
    promotionsInfo: 'Bonuses promotions',
    ...accountRouteNames,
    ...resultsRouteNames,
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/app/router/constants.ts